import React, { useState, useEffect, useRef } from 'react';
import { calculateDistance } from './locationHelper';
import comms from '../../comms';

const GeolocationHandler = ({ onLocationUpdate, onLocationError, setIsAccurate, onRawPosition, meterActive = false }) => {
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [showPreciseLocationPrompt, setShowPreciseLocationPrompt] = useState(false);
  const [showPreciseLocationModal, setShowPreciseLocationModal] = useState(false);

  const isiOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const [needsUserInteraction, setNeedsUserInteraction] = useState(isiOS);
  const lastLocation = useRef(null);
  const lastUpdateTime = useRef(0);

  // Keep latest meter-active flag in a ref so the watchPosition callback (set up once)
  // sees the current value without re-subscribing.
  const meterActiveRef = useRef(meterActive);
  useEffect(() => {
    meterActiveRef.current = meterActive;
  }, [meterActive]);
  
  // GPS sending state for dispatch screen
  const lastGpsSentLocation = useRef(null);
  const lastGpsSentTime = useRef(0);
  const gpsTimerRef = useRef(null);
  const watchIdRef = useRef(null);
  // Keep-alive poll while soft meter is running. Browsers (especially iOS Safari)
  // frequently let watchPosition go quiet after a few minutes of "no movement",
  // which leaves the meter receiving zero fixes → DIST stays at 0.0 even though
  // the meter is enabled. We defeat that with a periodic getCurrentPosition burst
  // for the entire lifetime of the meter run.
  const meterPollRef = useRef(null);

  // Refs for handlers used across effects (kept stable inside this component).
  const handlePositionUpdateRef = useRef(null);
  const onLocationErrorRef = useRef(onLocationError);
  useEffect(() => { onLocationErrorRef.current = onLocationError; }, [onLocationError]);

  // Send GPS with current location
  const sendCurrentGPS = () => {
    if (lastLocation.current) {
      comms.sendGPS(lastLocation.current.latitude, lastLocation.current.longitude, 0);
      lastGpsSentLocation.current = lastLocation.current;
      lastGpsSentTime.current = Date.now();
      console.log("GPS sent to server (timer):", { 
        latitude: lastLocation.current.latitude, 
        longitude: lastLocation.current.longitude 
      });
    }
  };

  // Start GPS timer to send every 4 minutes
  const startGPSTimer = () => {
    // Clear existing timer
    if (gpsTimerRef.current) {
      clearInterval(gpsTimerRef.current);
    }
    
    // Start new timer - send GPS every 4 minutes (240,000 milliseconds)
    gpsTimerRef.current = setInterval(() => {
      sendCurrentGPS();
    }, 240000);
  };

  const shouldUpdate = (newLocation) => {
    const now = Date.now();
    if (!lastLocation.current || !lastUpdateTime.current) return true;

    // Tighten cadence while a soft meter is running so the map and downstream
    // distance calc react to GPS movement quickly. Idle thresholds stay loose
    // to save battery.
    const timeThresholdMs = meterActiveRef.current ? 3000 : 18000;
    const distanceThresholdM = meterActiveRef.current ? 15 : 200;

    const timeDiff = now - lastUpdateTime.current;
    if (timeDiff >= timeThresholdMs) return true;

    const distance = calculateDistance(
      lastLocation.current.latitude,
      lastLocation.current.longitude,
      newLocation.latitude,
      newLocation.longitude
    );

    return distance >= distanceThresholdM;
  };

  const shouldSendGPS = (newLocation) => {
    const now = Date.now();
    
    // Always send GPS on first location fix
    if (!lastGpsSentLocation.current || !lastGpsSentTime.current) return true;

    const timeDiff = now - lastGpsSentTime.current;
    // Send GPS every 4 minutes (240,000 milliseconds)
    if (timeDiff >= 240000) return true;

    const distance = calculateDistance(
      lastGpsSentLocation.current.latitude,
      lastGpsSentLocation.current.longitude,
      newLocation.latitude,
      newLocation.longitude
    );
    
    // Send GPS after 1/3 mile (approximately 536 meters)
    return distance >= 536;
  };

  const handlePositionUpdate = (position) => {
    setNeedsUserInteraction(false);
    const { latitude, longitude, accuracy } = position.coords;
    // Real device speed (m/s) -> whole MPH for server/GPS-history reporting.
    const rawSpeed = position.coords.speed;
    const speedMs = Number.isFinite(rawSpeed) && rawSpeed >= 0 ? rawSpeed : null;
    const speedMph = comms.mpsToMph(speedMs);
    const newLocation = { latitude, longitude, accuracy };

    // Feed every GPS fix to SoftMeter unthrottled (before the UI throttle gate).
    // Skip invalid coords — NaN would poison odometer / fare math inside the meter.
    if (onRawPosition && Number.isFinite(latitude) && Number.isFinite(longitude)) {
      const spd = position.coords.speed;
      const speedMs = Number.isFinite(spd) && spd >= 0 ? spd : null;
      if (meterActiveRef.current) {
        // eslint-disable-next-line no-console
        console.log('[METER] GeolocationHandler raw fix →', {
          lat: latitude,
          lng: longitude,
          accuracy,
          speedMs,
          ts: position.timestamp,
        });
      }
      onRawPosition(latitude, longitude, speedMs, position.timestamp);
    }

    if (shouldUpdate(newLocation)) {
      lastLocation.current = newLocation;
      lastUpdateTime.current = Date.now();
      setLocation(newLocation);
      if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
        onLocationUpdate({ lat: latitude, lng: longitude, speed: speedMph });
      }
      
      console.log("location", newLocation);
      
      if (accuracy <= 100) {
        setIsAccurate(true);
      } else {
        setIsAccurate(false);  
      }
      
      if (isiOS && accuracy > 100) {
        setShowPreciseLocationPrompt(true);
      }

      // Check if we should send GPS update to server
      if (shouldSendGPS(newLocation)) {
        comms.sendGPS(latitude, longitude, speedMph);
        lastGpsSentLocation.current = newLocation;
        lastGpsSentTime.current = Date.now();
        console.log("GPS sent to server:", { latitude, longitude, speedMph });
      }

      // Start GPS timer if this is the first location fix
      if (!gpsTimerRef.current) {
        startGPSTimer();
      }
    }
  };

  // Expose handlePositionUpdate to other effects via a ref so they can reuse the
  // exact same throttling/meter-feeding pipeline as the watchPosition subscription.
  handlePositionUpdateRef.current = handlePositionUpdate;

  const getLocation = () => {
    if (!navigator.geolocation) {
      const errorMsg = 'Geolocation is not supported by your browser.';
      setError(errorMsg);
      if (onLocationError) onLocationError(errorMsg);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      handlePositionUpdate,
      (err) => {
        console.error(err);
        let errorMsg;
        switch (err.code) {
          case err.PERMISSION_DENIED:
            errorMsg = 'Permission denied. Please enable location access.';
            break;
          case err.POSITION_UNAVAILABLE:
            errorMsg = 'Location unavailable.';
            break;
          case err.TIMEOUT:
            errorMsg = 'Location request timed out.';
            break;
          default:
            errorMsg = 'Unknown error occurred.';
        }
        setError(errorMsg);
        if (onLocationError) onLocationError(errorMsg);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const handleRequestLocation = () => {
    getLocation();
  };

  // ── Meter-active GPS keep-alive ────────────────────────────────────────────
  // When the soft meter starts, we must guarantee a live stream of GPS fixes —
  // otherwise DIST sticks at 0.0 and the meter only accrues TIME. Browsers
  // (notably iOS Safari) routinely let watchPosition go silent after a stretch
  // of "no movement" or screen-off; the existing watchId is still registered
  // but the OS stops invoking it. We mitigate by:
  //   (a) Tearing down + re-subscribing watchPosition the moment the meter
  //       transitions to active (a fresh subscription almost always resumes
  //       fix delivery), and
  //   (b) Running a getCurrentPosition keep-alive every 6 s for the entire
  //       meter-active lifetime. shouldUpdate() throttles UI work, and the
  //       meter's own _gapPending logic handles duplicate fixes safely.
  useEffect(() => {
    if (!meterActive) {
      if (meterPollRef.current) {
        clearInterval(meterPollRef.current);
        meterPollRef.current = null;
        // eslint-disable-next-line no-console
        console.log('[METER] GeolocationHandler keep-alive poll STOPPED');
      }
      return undefined;
    }

    // eslint-disable-next-line no-console
    console.log('[METER] GeolocationHandler meter ACTIVE — restarting watch + starting keep-alive poll');

    // (a) Restart watchPosition so we get a fresh subscription that the OS
    // is committed to fulfilling.
    try {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      watchIdRef.current = navigator.geolocation.watchPosition(
        (pos) => handlePositionUpdateRef.current && handlePositionUpdateRef.current(pos),
        (err) => {
          console.error(err);
          const errorMsg = err.code === err.PERMISSION_DENIED
            ? 'Permission denied. Please enable location access.'
            : 'Location tracking error occurred.';
          setError(errorMsg);
          if (onLocationErrorRef.current) onLocationErrorRef.current(errorMsg);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } catch (e) {
      console.warn('GeolocationHandler: failed to restart watchPosition on meter start', e);
    }

    // (b) Keep-alive poll. Fires getCurrentPosition every 6 s while meter is
    // active. Each delivered fix flows through handlePositionUpdate → onRawPosition
    // (always, unthrottled) → SoftMeter.onLocationUpdate, and through
    // shouldUpdate() → onLocationUpdate (map center, throttled).
    if (meterPollRef.current) {
      clearInterval(meterPollRef.current);
    }
    const pollTick = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        // Don't poll while hidden — the visibilitychange handler will run its own
        // resume burst when we come back, and iOS rejects high-accuracy requests
        // from background pages anyway.
        return;
      }
      try {
        navigator.geolocation.getCurrentPosition(
          (pos) => handlePositionUpdateRef.current && handlePositionUpdateRef.current(pos),
          (err) => {
            // eslint-disable-next-line no-console
            console.warn('[METER] keep-alive getCurrentPosition failed', err && err.message);
          },
          { enableHighAccuracy: true, timeout: 8000, maximumAge: 3000 }
        );
      } catch (e) {
        // ignore — next tick will retry
      }
    };
    // First tick after a short delay so we don't double-fire alongside watchPosition's
    // initial callback.
    const firstTimer = setTimeout(pollTick, 1500);
    meterPollRef.current = setInterval(pollTick, 6000);

    return () => {
      clearTimeout(firstTimer);
      if (meterPollRef.current) {
        clearInterval(meterPollRef.current);
        meterPollRef.current = null;
      }
    };
  }, [meterActive]);

  useEffect(() => {
    if (!isiOS) {
      navigator.geolocation.getCurrentPosition(
        handlePositionUpdate,
        (err) => {
          console.error(err);
          const errorMsg = err.code === err.PERMISSION_DENIED
            ? 'Permission denied. Please enable location access.'
            : 'Location tracking error occurred.';
          setError(errorMsg);
          if (onLocationError) onLocationError(errorMsg);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    }

    watchIdRef.current = navigator.geolocation.watchPosition(
      handlePositionUpdate,
      (err) => {
        console.error(err);
        const errorMsg = err.code === err.PERMISSION_DENIED
          ? 'Permission denied. Please enable location access.'
          : 'Location tracking error occurred.';
        setError(errorMsg);
        if (onLocationError) onLocationError(errorMsg);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );

    // Resurrect the watch on background → foreground transitions. Many browsers
    // (notably iOS Safari and Chrome PWAs on Android) suspend or silently kill
    // watchPosition while the tab is hidden, so the existing watchId stops firing
    // even after the page becomes visible again. We:
    //   1) immediately request getCurrentPosition with a small maximumAge so a
    //      cached fix can prime the meter's gap-recovery anchor (it polls 6 s),
    //   2) tear down + restart watchPosition to guarantee a live subscription,
    //   3) run a short post-resume polling burst (getCurrentPosition every few seconds)
    //      because iOS in particular often takes 5–15 s to deliver fresh watchPosition
    //      fixes after a long suspension — the burst keeps the map and meter live
    //      during that wake-up window.
    const resumePollRef = { id: null, stopAt: 0 };
    const stopResumePoll = () => {
      if (resumePollRef.id) {
        clearInterval(resumePollRef.id);
        resumePollRef.id = null;
      }
    };
    const startResumePoll = () => {
      stopResumePoll();
      // Poll every 3 s for up to 30 s. shouldUpdate() in handlePositionUpdate gates
      // the per-fix UI work, so over-polling here is cheap.
      resumePollRef.stopAt = Date.now() + 30000;
      const tick = () => {
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
          stopResumePoll();
          return;
        }
        if (Date.now() >= resumePollRef.stopAt) {
          stopResumePoll();
          return;
        }
        try {
          navigator.geolocation.getCurrentPosition(
            handlePositionUpdate,
            (err) => {
              // eslint-disable-next-line no-console
              console.warn('[METER] resume-poll getCurrentPosition failed', err && err.message);
            },
            { enableHighAccuracy: true, timeout: 8000, maximumAge: 2000 }
          );
        } catch (e) {
          // ignore — next tick will retry
        }
      };
      // First tick after a short delay so the immediate primer call below isn't doubled.
      resumePollRef.id = setInterval(tick, 3000);
    };
    const handleVisibilityChange = () => {
      if (typeof document === 'undefined') return;
      if (document.visibilityState !== 'visible') {
        stopResumePoll();
        return;
      }
      // eslint-disable-next-line no-console
      console.log('[METER] GeolocationHandler visibilitychange → visible — resurrecting watch', {
        meterActive: meterActiveRef.current,
        hasWatch: watchIdRef.current !== null,
      });
      // Prime a fresh fix immediately (cached up to 5 s is fine; we just need to
      // unblock the meter's tryRecover loop that's polling for a post-resume fix).
      try {
        navigator.geolocation.getCurrentPosition(
          handlePositionUpdate,
          (err) => {
            // eslint-disable-next-line no-console
            console.warn('[METER] getCurrentPosition on resume failed', err && err.message);
          },
          { enableHighAccuracy: true, timeout: 8000, maximumAge: 5000 }
        );
      } catch (e) {
        console.warn('GeolocationHandler: getCurrentPosition threw on resume', e);
      }
      // Defer the watchPosition tear-down/restart by ~600 ms. iOS frequently has its
      // GPS chip still spinning up at the visibilitychange edge; subscribing too early
      // results in a watch that never emits. The deferred restart catches the chip
      // once it's awake. The resume-poll burst keeps fixes flowing in the meantime.
      const restartWatch = () => {
        try {
          if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
          }
          watchIdRef.current = navigator.geolocation.watchPosition(
            handlePositionUpdate,
            (err) => {
              console.error(err);
              const errorMsg = err.code === err.PERMISSION_DENIED
                ? 'Permission denied. Please enable location access.'
                : 'Location tracking error occurred.';
              setError(errorMsg);
              if (onLocationError) onLocationError(errorMsg);
            },
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0,
            }
          );
        } catch (e) {
          console.warn('GeolocationHandler: failed to restart watchPosition on resume', e);
        }
      };
      setTimeout(restartWatch, 600);
      // Keep polling for fresh fixes for ~30 s post-resume so the map and meter stay
      // live even if the restarted watchPosition doesn't fire promptly on iOS.
      startResumePoll();
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      // Clear GPS timer on cleanup
      if (gpsTimerRef.current) {
        clearInterval(gpsTimerRef.current);
      }
      stopResumePoll();
      if (meterPollRef.current) {
        clearInterval(meterPollRef.current);
        meterPollRef.current = null;
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, []);

  return (
    <div className="">

      {/* Overlay for permission request */}
      {needsUserInteraction && (
        <div className="absolute inset-0 bg-black bg-opacity-60 flex flex-col justify-center items-center z-20">
          <h2 className="text-white text-xl mb-4">We need your location</h2>
          <button
            onClick={handleRequestLocation}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg"
          >
            Grant Location Access
          </button>
          {error && (
            <p className="text-red-300 mt-4">{error}</p>
          )}
        </div>
      )}

      {/* Location display */}


      {/* Precise location warning for iOS */}
      {showPreciseLocationPrompt && (
        <button
          className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-2 rounded shadow z-10"
          onClick={() => setShowPreciseLocationModal(true)}
        >
          Improve Location Accuracy
        </button>
      )}

      {/* Modal for precise location instructions */}
      {showPreciseLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[2200]">
          <div className="bg-white p-6 rounded-lg w-11/12 md:w-1/3 text-center">
            <h2 className="text-xl font-bold mb-4">Enable Precise Location</h2>
            <p className="mb-4 text-gray-700">
              To get more accurate location, enable "Precise Location" for Safari:
            </p>
            <ol className="text-left text-gray-600 mb-4 space-y-2">
              <li>1. Open your iPhone Settings app</li>
              <li>2. Tap Privacy & Security &gt; Location Services</li>
              <li>3. Scroll down and select Safari Websites</li>
              <li>4. Make sure "While Using the App" is selected</li>
              <li>5. Turn on "Precise Location"</li>
            </ol>
            <button
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
              onClick={() => {
                setShowPreciseLocationPrompt(false);
                setShowPreciseLocationModal(false)
              }
            }
            >
              Got it!
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeolocationHandler;
